import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { PlaylistSummary, PlaylistDetail, Episode } from '../types';
import { useVideoStore } from './videoStore';

export type AppViewMode = 'list' | 'playlist-detail' | 'player';

export const usePlaylistStore = defineStore('playlist', () => {
  const playlists = ref<PlaylistSummary[]>([]);
  const currentPlaylist = ref<PlaylistDetail | null>(null);
  const playingEpisodeIndex = ref(-1);
  const playingVideoId = ref<string | null>(null);
  const viewMode = ref<AppViewMode>('list');
  const showListWhilePlaying = ref(true);
  const loading = ref(false);
  const error = ref<string | null>(null);

  function toggleListWhilePlaying() {
    showListWhilePlaying.value = !showListWhilePlaying.value;
  }

  const isPlaying = computed(() => viewMode.value === 'player');
  const showToolbar = computed(() => viewMode.value === 'list');
  const showListBelow = computed(() => viewMode.value === 'list' || (viewMode.value === 'player' && showListWhilePlaying.value));
  const showPlaylists = computed(() => showListBelow.value);
  const showGrid = computed(() => showListBelow.value);
  const showPagination = computed(() => showListBelow.value);
  const showStats = computed(() => showListBelow.value);
  const showPlaylistDetail = computed(() => viewMode.value === 'playlist-detail' || (viewMode.value === 'player' && showListWhilePlaying.value && currentPlaylist.value !== null));

  const currentEpisode = computed(() => {
    if (!currentPlaylist.value || playingEpisodeIndex.value < 0) return null;
    return currentPlaylist.value.episodes[playingEpisodeIndex.value] ?? null;
  });

  const canGoPrev = computed(() => {
    if (currentPlaylist.value && playingEpisodeIndex.value > 0) return true;
    const videoStore = useVideoStore();
    return videoStore.filteredVideos.findIndex((v) => v.id === playingVideoId.value) > 0;
  });

  const canGoNext = computed(() => {
    if (currentPlaylist.value && playingEpisodeIndex.value < currentPlaylist.value.episodes.length - 1) return true;
    const videoStore = useVideoStore();
    const idx = videoStore.filteredVideos.findIndex((v) => v.id === playingVideoId.value);
    return idx >= 0 && idx < videoStore.filteredVideos.length - 1;
  });

  function goToView(mode: AppViewMode) {
    viewMode.value = mode;
  }

  function openPlaylist(detail: PlaylistDetail) {
    currentPlaylist.value = detail;
    viewMode.value = 'playlist-detail';
  }

  function clearPlaylist() {
    currentPlaylist.value = null;
    playingEpisodeIndex.value = -1;
  }

  function playEpisode(index: number) {
    if (!currentPlaylist.value || !currentPlaylist.value.episodes[index]) return;
    playingEpisodeIndex.value = index;
    playingVideoId.value = currentPlaylist.value.episodes[index].file_id;
    viewMode.value = 'player';
  }

  function playAllEpisodes() {
    if (currentPlaylist.value?.episodes.length) {
      playEpisode(0);
    }
  }

  function playNext() {
    if (!currentPlaylist.value) return;
    if (playingEpisodeIndex.value < currentPlaylist.value.episodes.length - 1) {
      playEpisode(playingEpisodeIndex.value + 1);
    }
  }

  function playPrev() {
    if (!currentPlaylist.value) return;
    if (playingEpisodeIndex.value > 0) {
      playEpisode(playingEpisodeIndex.value - 1);
    }
  }

  function playStandaloneVideo(fileId: string) {
    playingVideoId.value = fileId;
    viewMode.value = 'player';
  }

  function goBack() {
    if (viewMode.value === 'player') {
      // 回到之前的视图
      viewMode.value = currentPlaylist.value ? 'playlist-detail' : 'list';
    } else if (viewMode.value === 'playlist-detail') {
    }
  }

  function onVideoEnded() {
    if (currentPlaylist.value && playingEpisodeIndex.value < currentPlaylist.value.episodes.length - 1) {
      playEpisode(playingEpisodeIndex.value + 1);
    }
  }

  return {
    playlists,
    currentPlaylist,
    playingEpisodeIndex,
    playingVideoId,
    viewMode,
    loading,
    error,
    isPlaying,
    showListWhilePlaying,
    showToolbar,
    showListBelow,
    showPlaylists,
    showGrid,
    showPagination,
    showStats,
    showPlaylistDetail,
    toggleListWhilePlaying,
    currentEpisode,
    canGoPrev,
    canGoNext,
    goToView,
    openPlaylist,
    clearPlaylist,
    playEpisode,
    playAllEpisodes,
    playNext,
    playPrev,
    playStandaloneVideo,
    goBack,
    onVideoEnded,
  };
});
